import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase environment variables not set. Auth will not work.')
}

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  }
)

/**
 * Supabase Ready Gate
 * 
 * The Supabase client fires onAuthStateChange events during its initialization
 * (specifically during _recoverAndRefresh and _initialize). Making database queries
 * inside these callbacks or before initialization completes can cause them to hang
 * indefinitely.
 * 
 * This mechanism ensures that any code waiting on `waitForSupabaseReady()` will
 * only proceed after the auth system has fully initialized.
 */
let supabaseReady = false
let resolveSupabaseReady: (() => void) | null = null

const supabaseReadyPromise = new Promise<void>((resolve) => {
  resolveSupabaseReady = resolve
})

/**
 * Wait for Supabase to be fully initialized before making queries.
 * This prevents queries from hanging when made during auth initialization.
 */
export async function waitForSupabaseReady(): Promise<void> {
  if (supabaseReady) return
  await supabaseReadyPromise
}

// Mark Supabase as ready after auth initialization completes
supabase.auth.onAuthStateChange((event) => {
  // INITIAL_SESSION fires after full initialization
  // SIGNED_IN may fire during initialization but we handle it as a fallback
  if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN') && !supabaseReady) {
    // Small delay to ensure auth is fully initialized
    setTimeout(() => {
      supabaseReady = true
      if (resolveSupabaseReady) {
        resolveSupabaseReady()
      }
    }, 100)
  }
})
